/**
 * Optimistic UI update utilities
 * 
 * Provides helpers for implementing optimistic updates with undo functionality.
 * Follows Gmail/Superhuman-style instant feedback patterns.
 */

export interface OptimisticUpdateOptions<T, R = void> {
  /**
   * Function to perform the optimistic update on local state
   */
  optimisticUpdate: () => void;
  
  /**
   * Function to revert the optimistic update (called on error or undo)
   */
  revertUpdate: () => void;
  
  /**
   * API call to perform the actual update
   */
  apiCall: () => Promise<R>;
  
  /**
   * Success message to show in toast
   */
  successMessage: string;
  
  /**
   * Error message to show if API call fails
   */
  errorMessage?: string;
  
  /**
   * Whether to show undo button (default: true)
   */
  showUndo?: boolean;
  
  /**
   * Custom undo message (default: uses successMessage)
   */
  undoMessage?: string;
  
  /**
   * Callback to execute on successful undo
   */
  onUndo?: () => void;
  
  /**
   * Callback to execute on successful API call
   */
  onSuccess?: (result: R) => void;
  
  /**
   * Callback to execute on API error
   */
  onError?: (error: any) => void;
}

/**
 * Execute an optimistic update with automatic error handling and undo support
 * 
 * @example
 * ```typescript
 * await performOptimisticUpdate({
 *   optimisticUpdate: () => setEmails(prev => prev.filter(e => e.id !== emailId)),
 *   revertUpdate: () => setEmails(prev => [...prev, originalEmail]),
 *   apiCall: () => archiveEmail(emailId),
 *   successMessage: 'Email archived',
 *   errorMessage: 'Failed to archive email',
 * });
 * ```
 */
export async function performOptimisticUpdate<T, R = void>(
  options: OptimisticUpdateOptions<T, R>
): Promise<void> {
  const {
    optimisticUpdate,
    revertUpdate,
    apiCall,
    successMessage,
    errorMessage = 'Operation failed',
    showUndo = true,
    undoMessage,
    onUndo,
    onSuccess,
    onError,
  } = options;

  // Perform optimistic update immediately
  optimisticUpdate();

  try {
    // Execute API call
    const result = await apiCall();
    
    // Call success callback if provided
    if (onSuccess) {
      onSuccess(result);
    }

    // Show success toast with undo button (handled by caller using toastWithUndo)
    // This function doesn't show the toast itself - that's done by the caller
    // to allow for custom toast implementations
    
  } catch (error: any) {
    // Revert optimistic update on error
    revertUpdate();
    
    // Call error callback if provided
    if (onError) {
      onError(error);
    }
    
    // Re-throw error so caller can handle toast
    throw error;
  }
}

/**
 * Create an undo function for an optimistic update
 * 
 * @param revertUpdate - Function to revert the optimistic update
 * @param apiCall - API call to revert the change on the server
 * @param successMessage - Message to show on successful undo
 * @returns Undo function that can be called to revert the change
 */
export function createUndoFunction(
  revertUpdate: () => void,
  apiCall: () => Promise<any>,
  successMessage: string = 'Action undone'
): () => Promise<void> {
  return async () => {
    // Revert local state immediately
    revertUpdate();
    
    try {
      // Call API to revert on server
      await apiCall();
      
      // Note: Toast is shown by the caller
    } catch (error) {
      console.error('Failed to undo action:', error);
      // Re-throw so caller can handle error toast
      throw error;
    }
  };
}

